import { useEffect, useState } from 'react';

import { datadogRum } from 'root/datadog/datadogRum';

import { AggregateSaleData } from '../state/AggregateSaleData';

export function useAggregateSale(saleID?: string) {
  const accountID = window.merchantos.account.id;

  const [aggregateSale, setAggregateSale] = useState<AggregateSaleData | null>(null);
  const [loading, setLoading] = useState<boolean>(!!saleID);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    // handle missing saleID clear state and stop loading
    if (!saleID) {
      setAggregateSale(null);
      setError(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    // fetch everything on the register including any present sales, layaways, and special orders
    fetch(`/API/V3/Account/${accountID}/DisplayTemplate/Sale/${saleID}.json?load_relations=all`)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((data: AggregateSaleData) => {
        const saleData = data?.Sale;
        const aggregateSaleData: AggregateSaleData = {
          Sale: {
            saleID: saleData?.saleID ?? '',
            registerID: saleData?.registerID ?? '',
            displayableSubtotal: saleData?.displayableSubtotal ?? '',
            calcItemFees: saleData?.calcItemFees ?? '',
            calcDiscount: saleData?.calcDiscount ?? '',
            calcTotal: saleData?.calcDiscount ?? '',
            employeeID: saleData?.employeeID ?? '',
            ...saleData,
            Customer: saleData?.Customer
              ? {
                  ...saleData.Customer,
                  Layaways: saleData.Customer.Layaways?.SaleLine
                    ? { SaleLine: saleData.Customer.Layaways.SaleLine }
                    : undefined,
                  SpecialOrders: saleData.Customer.SpecialOrders?.SaleLine
                    ? { SaleLine: saleData.Customer.SpecialOrders.SaleLine }
                    : undefined,
                }
              : undefined,
            SaleLines: saleData?.SaleLines?.SaleLine
              ? { SaleLine: saleData.SaleLines.SaleLine }
              : undefined,
            m: saleData?.m ? { itemFeesSubtotal: saleData.m.itemFeesSubtotal } : undefined,
            TaxClassTotals: saleData?.TaxClassTotals,
            taxTotal: saleData?.taxTotal ?? '',
          },
        };
        setAggregateSale(aggregateSaleData);
      })
      .catch((err) => {
        const e = err instanceof Error ? err : new Error(String(err));
        setError(e);
        datadogRum.addError(e, {
          message: `Failed to fetch aggregate sale for saleID: ${saleID} and accountID: ${accountID}`,
          accountID,
          saleID,
        });
      })
      .finally(() => setLoading(false));
  }, [accountID, saleID]);

  return { aggregateSale, loading, error };
}
